import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/* -------------- YouTube ID helper -------------- */
export function extractYoutubeId(input) {
  const raw = (input || "").trim();
  if (!raw) return "";
  if (/^[A-Za-z0-9_-]{11}$/.test(raw)) return raw;
  const m = raw.match(/(?:v=|\/embed\/|youtu\.be\/|\/shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : "";
}

/* -------------- YouTube Player with tracking --------------
   Panggil onEnded ketika video selesai (state=0) atau progres >= 95%. */
export function YouTubeWatcher({ videoId, onEnded, onProgress }) {
  const holderRef = useRef(null);
  const playerRef = useRef(null);
  const pollRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    doneRef.current = false;
    // Load API once
    const ensureApi = () =>
      new Promise((resolve) => {
        if (window.YT && window.YT.Player) return resolve();
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(); };
        if (!document.querySelector('script[data-yt-api]')) {
          const s = document.createElement("script");
          s.src = "https://www.youtube.com/iframe_api";
          s.async = true;
          s.setAttribute("data-yt-api", "1");
          document.body.appendChild(s);
        }
      });

    let cancelled = false;
    ensureApi().then(() => {
      if (cancelled || !holderRef.current) return;
      playerRef.current = new window.YT.Player(holderRef.current, {
        videoId,
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.ENDED && !doneRef.current) {
              doneRef.current = true;
              onEnded && onEnded();
            }
          },
        },
      });
      // Poll progress every 2 s → fallback ended when >= 95 % watched
      pollRef.current = setInterval(() => {
        try {
          const p = playerRef.current;
          if (!p || !p.getDuration) return;
          const dur = p.getDuration();
          const cur = p.getCurrentTime();
          if (dur > 0) {
            const pct = cur / dur;
            onProgress && onProgress(pct);
            if (pct >= 0.95 && !doneRef.current) {
              doneRef.current = true;
              onEnded && onEnded();
            }
          }
        } catch (_) { /* ignore */ }
      }, 2000);
    });

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      try { playerRef.current && playerRef.current.destroy && playerRef.current.destroy(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  return (
    <div className="aspect-video rounded-xl overflow-hidden bg-black shadow-lg" data-testid="lms-yt-holder">
      <div ref={holderRef} className="w-full h-full" />
    </div>
  );
}

/* -------------- Status badge for rekap Kepsek -------------- */
export function LmsStatusBadge({ status }) {
  const map = {
    "Belum Nonton": "pp-badge-neutral",
    "Sudah Nonton": "pp-badge-warning",
    "Sudah Tes (Belum Lulus)": "pp-badge-danger",
    "Lulus": "pp-badge-success",
  };
  return <span className={map[status] || "pp-badge-neutral"}>{status}</span>;
}

/* -------------- Empty state helper -------------- */
export function LmsEmpty({ msg }) {
  return <div className="text-center py-10 text-slate-400 italic">{msg}</div>;
}

/* -------------- Tiny form utils used across pages -------------- */
export function useDebouncedCallback(fn, delay = 500) {
  const t = useRef(null);
  return (...args) => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => fn(...args), delay);
  };
}

export function notifyOnce(msg) {
  toast.success(msg);
}
